import {
  BiBriefcase,
  BiBuildings,
  BiFile,
  BiLinkAlt,
  BiMap,
  BiRupee
} from 'react-icons/bi';
import { activeImportValues } from '../../../utils/organisationSettings';

export const MASTER_UPLOADS = [
  {
    id: 'customers',
    title: 'Clients / Authorities',
    description: 'Authorities and private clients.',
    icon: BiBuildings,
    fileName: 'dhruv-customers-template.xlsx',
    columns: [
      ['customerNo', 'Customer no'],
      ['name', 'Name'],
      ['shortName', 'Short name'],
      ['category', 'Category'],
      ['pan', 'PAN'],
      ['gstin', 'GSTIN'],
      ['currency', 'Currency'],
      ['status', 'Status'],
      ['contactName', 'Contact name'],
      ['email', 'Email'],
      ['phone', 'Phone'],
      ['website', 'Website'],
      ['portalUrl', 'Portal URL'],
      ['refundFollowUpDays', 'Refund follow-up days'],
      ['address', 'Address'],
      ['city', 'City'],
      ['state', 'State'],
      ['notes', 'Notes']
    ],
    notes: [
      'Name and Category are required. Leave Customer no blank to assign the next number.',
      'Category must be one of the customer categories under Settings, Organisation.',
      'Currency must be one enabled under Settings, Organisation. Blank uses the organisation currency.',
      'Status: active or inactive. Blank means active.',
      'PAN looks like AAAAA9999A. GSTIN is optional.',
      'Refund follow-up days is the client default. Leave it blank to keep the pursuit reminder rule. A tender instrument can still set its own number.'
    ]
  },
  {
    id: 'services',
    title: 'Services',
    description: 'Services the firm offers.',
    icon: BiBriefcase,
    fileName: 'dhruv-services-template.xlsx',
    columns: [
      ['serviceNo', 'Service no'],
      ['code', 'Code'],
      ['name', 'Name'],
      ['category', 'Category'],
      ['billingBasis', 'Billing basis'],
      ['typicalDuration', 'Typical duration'],
      ['deliverable', 'Deliverable'],
      ['description', 'Description'],
      ['notes', 'Notes'],
      ['status', 'Status']
    ],
    notes: [
      'Name and Category are required. Leave Service no and Code blank to assign them.',
      'Category must be one of the service categories under Settings, Organisation.',
      'Billing basis must be one of the billing values under Settings, Organisation. Blank uses the first of those values.',
      'Status: active or inactive. Blank means active.'
    ]
  },
  {
    id: 'sectors',
    title: 'Sectors',
    description: 'Sectors the firm pursues.',
    icon: BiMap,
    fileName: 'dhruv-sectors-template.xlsx',
    columns: [
      ['sectorNo', 'Sector no'],
      ['code', 'Code'],
      ['name', 'Name'],
      ['group', 'Group'],
      ['typicalClients', 'Typical clients'],
      ['assetFocus', 'Asset focus'],
      ['description', 'Description'],
      ['notes', 'Notes'],
      ['status', 'Status']
    ],
    notes: [
      'Name and Group are required. Leave Sector no and Code blank to assign them.',
      'Group must be one of the sector groups under Settings, Organisation.',
      'Status: active or inactive. Blank means active.'
    ]
  },
  {
    id: 'tender-types',
    title: 'Tender types',
    description: 'The forms a bid can take.',
    icon: BiFile,
    fileName: 'dhruv-tender-types-template.xlsx',
    columns: [
      ['typeNo', 'Type no'],
      ['code', 'Code'],
      ['name', 'Name'],
      ['group', 'Group'],
      ['typicalRoute', 'Typical route'],
      ['submission', 'Submission'],
      ['description', 'Description'],
      ['notes', 'Notes'],
      ['status', 'Status']
    ],
    notes: [
      'Name and Group are required. Leave Type no and Code blank to assign them.',
      'Group must be one of the tender type groups under Settings, Organisation.',
      'Status: active or inactive. Blank means active.'
    ]
  },
  {
    id: 'sources',
    title: 'Sources',
    description: 'Portals and channels where opportunities are found.',
    icon: BiLinkAlt,
    fileName: 'dhruv-sources-template.xlsx',
    columns: [
      ['sourceNo', 'Source no'],
      ['code', 'Code'],
      ['name', 'Name'],
      ['group', 'Group'],
      ['url', 'Web address'],
      ['coverage', 'Coverage'],
      ['description', 'Description'],
      ['notes', 'Notes'],
      ['status', 'Status']
    ],
    notes: [
      'Name and Group are required. Leave Source no and Code blank to assign them.',
      'Group must be one of the source groups under Settings, Organisation.',
      'Web address, when filled, starts with http:// or https://.',
      'Status: active or inactive. Blank means active.'
    ]
  },
  {
    id: 'rates',
    title: 'Costing Rates',
    description: 'Internal effort-cost and charge-out rates by designation.',
    icon: BiRupee,
    fileName: 'dhruv-rates-template.xlsx',
    columns: [
      ['designation', 'Designation'],
      ['purpose', 'Purpose'],
      ['amount', 'Amount (INR)'],
      ['rateBasis', 'Basis'],
      ['effectiveFrom', 'Effective from'],
      ['status', 'Status']
    ],
    notes: [
      'Designation, Purpose, Amount, Basis, and Effective from are required.',
      'Purpose: Internal effort cost or Charge-out. A blank purpose is rejected.',
      'Basis: hourly or monthly. A blank basis is rejected.',
      'Amount is in rupees, such as 1500.',
      'Effective from is a date, such as 2026-04-01.',
      'Choose Create or Update before uploading. Update changes the matching designation, purpose, basis, and date. It does not create a second rate.'
    ]
  },
  {
    id: 'turnovers',
    title: 'Annual turnover',
    description: 'Turnover by financial year and business category.',
    icon: BiRupee,
    fileName: 'dhruv-turnover-template.xlsx',
    columns: [
      ['financialYear', 'Financial year'],
      ['category', 'Business category'],
      ['currency', 'Currency'],
      ['amount', 'Amount'],
      ['verificationStatus', 'Verification'],
      ['notes', 'Notes']
    ],
    notes: [
      'Financial year looks like 2024-25. Category is the business the figure belongs to.',
      'Amount is required. A blank amount is rejected and is not stored as zero.',
      'Currency must be one enabled for tenders. Verification: unverified, verified, or rejected.',
      'Choose Create or Update before uploading. Create refuses a year and category that already exists. Update changes that same year and category and does not add a second row.',
      'A repeated row or a blank amount stops the file. Nothing from that file is saved.'
    ]
  },
  {
    id: 'project-experience',
    title: 'Assignment experience',
    description: 'Completed and ongoing assignments in the knowledge bank.',
    icon: BiBriefcase,
    fileName: 'dhruv-assignment-experience-template.xlsx',
    columns: [
      ['projectName', 'Project name'],
      ['client', 'Client'],
      ['sector', 'Sector'],
      ['serviceType', 'Service'],
      ['projectLocation', 'Location'],
      ['startDate', 'Start date'],
      ['completionDate', 'Completion date'],
      ['completionStatus', 'Completion status'],
      ['roadLengthKm', 'Road length km'],
      ['laneConfiguration', 'Lanes'],
      ['civilProjectValue', 'Civil project value'],
      ['consultancyFee', 'Consultancy fee'],
      ['participationShare', 'Dhruv share percent'],
      ['firmRole', 'Dhruv role']
    ],
    notes: [
      'Project name is required. Completion status is completed, ongoing, or blank.',
      'An ongoing assignment cannot have a completion date.',
      'Civil project value is the works value. Consultancy fee is Dhruv’s fee. Leave either blank when it is unknown.',
      'Share is 0 to 100, or blank.',
      'Choose Create or Update before uploading. The matching key is project name, client, and start date.',
      'An inactive service or sector cannot be chosen for a new row. A repeated row or an invalid date stops the file, and nothing from that file is saved.'
    ]
  },
  flatList('document-types', 'Document types', 'Document type names. A type does not grant access.', 'dhruv-document-types-template.xlsx', true),
  flatList('personnel-designations', 'Designations', 'Job titles. These are not application access roles.', 'dhruv-designations-template.xlsx', false),
  flatList('cost-heads', 'Cost heads', 'Estimate heads. Basis must be monthly, hourly, lump_sum, or unit.', 'dhruv-cost-heads-template.xlsx', false),
  flatList('instrument-types', 'Security / instrument types', 'EMD, bid security, performance BG, and other. Lifecycle states are not in this file.', 'dhruv-instrument-types-template.xlsx', false),
  flatList('outcome-reasons', 'Outcome reasons', 'Reasons for lost, withdrawn, cancelled, or no_go.', 'dhruv-outcome-reasons-template.xlsx', false)
];

export function describeMaster(master, lists) {
  if (!lists) return master;
  const customerCategories = activeImportValues(lists.customerCategories);
  const serviceCategories = activeImportValues(lists.serviceCategories);
  const serviceBilling = activeImportValues(lists.serviceBilling);
  const sectorGroups = activeImportValues(lists.sectorGroups);
  const tenderTypeGroups = activeImportValues(lists.tenderTypeGroups);
  const sourceGroups = activeImportValues(lists.sourceGroups);
  const notes = master.notes.map((line) => {
    if (master.id === 'customers' && line.startsWith('Category must')) {
      return `Category: ${customerCategories.join(', ')}.`;
    }
    if (master.id === 'services' && line.startsWith('Category must')) {
      return `Category: ${serviceCategories.join(', ')}.`;
    }
    if (master.id === 'services' && line.startsWith('Billing basis must')) {
      return `Billing basis: ${serviceBilling.join(', ')}. Blank means ${serviceBilling[0]}.`;
    }
    if (master.id === 'sectors' && line.startsWith('Group must')) {
      return `Group: ${sectorGroups.join(', ')}.`;
    }
    if (master.id === 'tender-types' && line.startsWith('Group must')) {
      return `Group: ${tenderTypeGroups.join(', ')}.`;
    }
    if (master.id === 'sources' && line.startsWith('Group must')) {
      return `Group: ${sourceGroups.join(', ')}.`;
    }
    return line;
  });
  return { ...master, notes };
}

function flatList(id, title, description, fileName, withAreas) {
  const columns = [
    ['code', 'Code'],
    ['label', 'Name'],
    ['description', 'Description'],
    ['status', 'Status']
  ];
  if (withAreas) columns.splice(3, 0, ['applicableAreas', 'Applicable areas']);
  if (id === 'cost-heads') columns.splice(3, 0, ['category', 'Category'], ['basis', 'Basis']);
  if (id === 'outcome-reasons') columns.splice(3, 0, ['outcomes', 'Applicable outcomes']);
  return {
    id,
    title,
    description,
    icon: BiFile,
    fileName,
    columns,
    notes: [
      'Code and Name are required. Choose Create or Update before uploading.',
      'Create rejects a code that already exists. Update changes that code and does not add a second row.',
      'Status: active or inactive. Blank means active.',
      'Applicable areas, when used, are comma-separated codes such as tender_documents, knowledge.',
      'Cost head category: personnel, travel, accommodation, setup, overhead, other.',
      'Cost head basis: monthly, hourly, lump_sum, or unit.',
      'Outcome values: lost, withdrawn, cancelled, no_go. Separate more than one with a comma.'
    ]
  };
}
